import React, { useState, useEffect, useRef } from 'react';
import './MenuBar.css';
import logo from '../img/logo3.png';
import logoWhite from '../img/logo-white.png';
import { useAuth } from '../contexts/AuthContext';
import { useScreenDimensions } from '../hooks/useScreenDimensions';
import MenuDropdown, { MenuItem } from './MenuDropdown';
import MiniMusicPlayer from './MiniMusicPlayer';
import MusicPlayerTooltip from './MusicPlayerTooltip';

interface MenuBarProps {
  focusedApp?: string;
  focusedUserAppId?: string | null;
  onMenuAction?: (action: string, data?: any) => void;
  hasPlayedOnce?: boolean;
  isPlaying?: boolean;
  currentTrack?: string;
  onPlayPause?: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  showMusicPlayerTooltip?: boolean;
  onDismissMusicPlayerTooltip?: () => void;
}

const MenuBar: React.FC<MenuBarProps> = ({
  focusedApp,
  focusedUserAppId,
  onMenuAction,
  hasPlayedOnce = false,
  isPlaying = false,
  currentTrack = '',
  onPlayPause = () => {},
  onNext = () => {},
  onPrev = () => {},
  showMusicPlayerTooltip = false,
  onDismissMusicPlayerTooltip = () => {}
}) => {
  const [currentTime, setCurrentTime] = useState('');
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const musicPlayerRef = useRef<HTMLDivElement | null>(null);
  const menuBarWrapperRef = useRef<HTMLDivElement | null>(null);
  const { currentUser, userHandle, logout } = useAuth();
  const screenDimensions = useScreenDimensions();

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const timeString = now.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      setCurrentTime(timeString);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, []);

  // Calculate tooltip position when it should be shown
  useEffect(() => {
    if (showMusicPlayerTooltip && musicPlayerRef.current && menuBarWrapperRef.current) {
      const iconRect = musicPlayerRef.current.getBoundingClientRect();
      const wrapperRect = menuBarWrapperRef.current.getBoundingClientRect();

      // Calculate position relative to the wrapper (not viewport)
      const iconRelativeLeft = iconRect.left - wrapperRect.left;
      const iconRelativeBottom = iconRect.bottom - wrapperRect.top;

      // Calculate tooltip width and position it centered below the icon
      const tooltipWidth = 240;
      const iconCenterX = iconRelativeLeft + iconRect.width / 2;
      const tooltipX = iconCenterX - tooltipWidth / 2;

      // Position tooltip centered below icon
      const adjustedX = screenDimensions.isMobile ? tooltipX - 10 : tooltipX;
      const tooltipY = screenDimensions.isMobile ? iconRelativeBottom + 2 : iconRelativeBottom + 10;

      console.log('Tooltip position (relative to wrapper):', { x: adjustedX, y: tooltipY });
      setTooltipPosition({ x: adjustedX, y: tooltipY });
    }
  }, [showMusicPlayerTooltip, screenDimensions.isMobile]);

  // Define menu items for different apps
  const getMenuItems = () => {
    // Handle song folders (01 - la paix, 02 - your orbit, etc.)
    if (focusedApp?.match(/^\d{2}\s*-\s*/)) {
      return ['File', 'Edit', 'View', 'Play'];
    }

    // Handle image preview windows
    if (focusedApp?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      return ['File', 'View', 'Tools'];
    }

    // Handle video preview windows
    if (focusedApp?.match(/\.(mp4|mov|avi|webm)$/i)) {
      return ['File', 'View', 'Playback'];
    }

    switch (focusedApp) {
      case 'in orbit':
        return ['File', 'Play', 'View'];
      case 'File Browser':
      case 'In Orbit':
      case 'Trash':
        return ['File', 'Edit', 'View', 'Go'];
      case 'lyrics.txt':
      case 'credits.txt':
      case 'abandoned.txt':
        return ['File', 'Edit', 'Format'];
      case 'Photo Booth':
        return ['File', 'Camera', 'Effects'];
      case 'Garden':
        return ['File', 'Garden', 'Settings'];
      case 'orbit.tv':
        return ['File', 'Channel', 'View'];
      case 'Guestbook':
        return ['File', 'Messages', 'View'];
      case 'adhiv.com':
        return ['File', 'Navigate', 'View'];
      case 'MacPaint':
        return ['File', 'Edit', 'Tools', 'Paint'];
      case 'Genius':
        return ['File', 'View', 'Play'];
      default:
        return ['File', 'Edit', 'View'];
    }
  };

  const menuItems = getMenuItems();

  // Get dropdown menu content based on focused app and menu name
  const getDropdownItems = (menuName: string): MenuItem[] => {
    // Logo menu
    if (menuName === 'logo') {
      return [
        {
          label: 'About OrbitOS',
          action: () => {
            onMenuAction?.('openOrbitOS');
            setOpenMenu(null);
          }
        },
        {
          label: 'Login/Signup',
          disabled: !!currentUser,
          action: () => {
            onMenuAction?.('openLoginSignup');
            setOpenMenu(null);
          }
        }
      ];
    }

    // Music player menu
    if (menuName === 'music-player') {
      return [
        {
          label: (
            <div className="mini-music-controls">
              <button className="mini-control-btn" onClick={(e) => { e.stopPropagation(); onPrev(); }}>
                <img src={require('../img/skip.png')} alt="Previous" className="flip-horizontal" />
              </button>
              <button className="mini-control-btn" onClick={(e) => { e.stopPropagation(); onPlayPause(); }}>
                <img src={isPlaying ? require('../img/pause.png') : require('../img/play.png')} alt={isPlaying ? 'Pause' : 'Play'} />
              </button>
              <button className="mini-control-btn" onClick={(e) => { e.stopPropagation(); onNext(); }}>
                <img src={require('../img/skip.png')} alt="Next" />
              </button>
            </div>
          ) as any,
          noHover: true,
          action: () => {} // No-op, buttons handle their own actions
        },
        { divider: true },
        {
          label: (
            <div className="mini-now-playing">
              <div className="now-playing-content">
                <div className="now-playing-text">
                  <div className="now-playing-label">Now Playing:</div>
                  <div className="now-playing-track">{currentTrack}</div>
                </div>
                {isPlaying && (
                  <div className="playing-animation">
                    <div className="bar bar1"></div>
                    <div className="bar bar2"></div>
                    <div className="bar bar3"></div>
                  </div>
                )}
              </div>
            </div>
          ) as any,
          action: () => {
            onMenuAction?.('openMusicPlayer');
            setOpenMenu(null);
          }
        }
      ];
    }

    // User handle menu
    if (menuName === 'user-handle') {
      return [
        {
          label: 'Logout',
          action: async () => {
            try {
              await logout();
              setOpenMenu(null);
            } catch (error) {
              console.error('Error logging out:', error);
            }
          }
        }
      ];
    }

    if (focusedApp === 'Photo Booth') {
      switch (menuName) {
        case 'File':
          return [
            { label: 'Download All Photos', action: () => onMenuAction?.('downloadAllPhotos') },
            { divider: true },
            { label: 'Close Window', action: () => onMenuAction?.('closeWindow'), shortcut: '⌥W' }
          ];
        case 'Camera':
          return [
            { label: 'Take Photo', disabled: true },
            { label: 'Take Four Photos', disabled: true }
          ];
        case 'Effects':
          return [
            { label: 'Normal', disabled: true },
            { label: 'Sepia', disabled: true },
            { label: 'Black & White', disabled: true }
          ];
        default:
          return [];
      }
    }

    if (focusedApp === 'in orbit') {
      switch (menuName) {
        case 'File':
          return [
            { label: 'Open Folder...', action: () => onMenuAction?.('openFolder', 'In Orbit') },
            { divider: true },
            { label: 'Close Window', action: () => onMenuAction?.('closeWindow'), shortcut: '⌥W' }
          ];
        case 'Play':
          return [
            { label: 'Play/Pause', action: () => onMenuAction?.('playPause'), shortcut: '⌥Space' },
            { label: 'Next Track', action: () => onMenuAction?.('nextTrack'), shortcut: '⌥→' },
            { label: 'Previous Track', action: () => onMenuAction?.('prevTrack'), shortcut: '⌥←' }
          ];
        case 'Tracks':
          return [
            { label: '01 - la paix', action: () => onMenuAction?.('playTrack', 0) },
            { label: '02 - your orbit', action: () => onMenuAction?.('playTrack', 1) },
            { label: '03 - things change', action: () => onMenuAction?.('playTrack', 2) },
            { label: '04 - everytime', action: () => onMenuAction?.('playTrack', 3) },
            { label: '05 - need ya', action: () => onMenuAction?.('playTrack', 4) },
            { label: '06 - simmering', action: () => onMenuAction?.('playTrack', 5) }
          ];
        case 'View':
          return [
            { label: 'View Album Art', action: () => onMenuAction?.('viewAlbumArt') },
            { divider: true },
            { label: 'Show Vinyl Animation', disabled: true }
          ];
      }
    }

    // Default generic menus for other apps (visual only for now)
    switch (menuName) {
      case 'File':
        return [
          { label: 'Close Window', action: () => onMenuAction?.('closeWindow'), shortcut: '⌥W' }
        ];
      case 'Edit':
        return [
          { label: 'Undo', disabled: true, shortcut: '⌥Z' },
          { label: 'Cut', disabled: true, shortcut: '⌥X' },
          { label: 'Copy', disabled: true, shortcut: '⌥C' },
          { label: 'Paste', disabled: true, shortcut: '⌥V' }
        ];
      case 'View':
        const viewItems: MenuItem[] = [
          { label: 'Zoom In', disabled: true },
          { label: 'Zoom Out', disabled: true }
        ];
        // Add "Open App" option if a published user app is focused
        if (focusedUserAppId) {
          viewItems.push(
            { divider: true },
            { 
              label: 'Open App in New Tab', 
              action: () => window.open(`/app/${focusedUserAppId}`, '_blank')
            }
          );
        }
        return viewItems;
      default:
        return [];
    }
  };

  const handleMenuClick = (menuName: string, event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();

    // For music player, position dropdown to the left (align right edge)
    if (menuName === 'music-player') {
      // Position so the dropdown's right edge (including shadow) aligns with the icon's right edge
      const dropdownContentWidth = 200; // The actual content width
      const rightEdge = rect.right;
      // Align right edge with icon, accounting for shadow
      const leftPosition = rightEdge - dropdownContentWidth;
      setMenuPosition({ x: Math.max(12, leftPosition), y: rect.bottom });

      // Dismiss tooltip when opening music player dropdown
      if (showMusicPlayerTooltip) {
        onDismissMusicPlayerTooltip?.();
      }
    } else {
      setMenuPosition({ x: rect.left, y: rect.bottom });
    }

    setOpenMenu(openMenu === menuName ? null : menuName);
  };

  const handleMenuHover = (menuName: string, event: React.MouseEvent<HTMLDivElement>) => {
    // Only auto-open on hover if a menu is already open
    if (openMenu) {
      const rect = event.currentTarget.getBoundingClientRect();

      // For music player, position dropdown to the left (align right edge)
      if (menuName === 'music-player') {
        // Position so the dropdown's right edge (including shadow) aligns with the icon's right edge
        const dropdownContentWidth = 200; // The actual content width
        const rightEdge = rect.right;
        // Align right edge with icon, accounting for shadow
        const leftPosition = rightEdge - dropdownContentWidth;
        setMenuPosition({ x: Math.max(12, leftPosition), y: rect.bottom });
      } else {
        setMenuPosition({ x: rect.left, y: rect.bottom });
      }

      setOpenMenu(menuName);
    }
  };

  return (
    <div ref={menuBarWrapperRef} style={{ position: 'relative' }}>
      <div className="menu-bar">
        <div className="menu-bar-left">
          <div
            className={`menu-logo-container ${openMenu === 'logo' ? 'active' : ''}`}
            onClick={(e) => handleMenuClick('logo', e)}
            onMouseEnter={(e) => handleMenuHover('logo', e)}
            ref={(el) => { menuRefs.current['logo'] = el; }}
          >
            <img
              src={openMenu === 'logo' ? logoWhite : logo}
              alt="Logo"
              className="menu-logo"
            />
          </div>
          {menuItems.map((item, index) => (
            <div
              key={index}
              ref={(el) => { menuRefs.current[item] = el; }}
              className={`menu-item ${openMenu === item ? 'active' : ''}`}
              onClick={(e) => handleMenuClick(item, e)}
              onMouseEnter={(e) => handleMenuHover(item, e)}
            >
              {item}
            </div>
          ))}
        </div>
        <div className="menu-bar-right">
          {hasPlayedOnce && (
            <MiniMusicPlayer
              isPlaying={isPlaying}
              currentTrack={currentTrack}
              onPlayPause={onPlayPause}
              onNext={onNext}
              onPrev={onPrev}
              isOpen={openMenu === 'music-player'}
              onToggle={(e) => handleMenuClick('music-player', e)}
              onHover={(e) => handleMenuHover('music-player', e)}
              onClose={() => setOpenMenu(null)}
              menuPosition={menuPosition}
              isMobile={screenDimensions.isMobile}
              iconRef={musicPlayerRef}
            />
          )}
          {userHandle && (
            <div
              ref={(el) => { menuRefs.current['user-handle'] = el; }}
              className={`user-handle ${openMenu === 'user-handle' ? 'active' : ''}`}
              onClick={(e) => handleMenuClick('user-handle', e)}
              onMouseEnter={(e) => handleMenuHover('user-handle', e)}
            >
              @{userHandle}
            </div>
          )}
          <div className="menu-time">{currentTime}</div>
        </div>
      </div>

      {openMenu && (
        <MenuDropdown
          items={getDropdownItems(openMenu)}
          onClose={() => setOpenMenu(null)}
          position={menuPosition}
          className={openMenu === 'music-player' ? 'mini-music-dropdown' : ''}
        />
      )}

      {showMusicPlayerTooltip && hasPlayedOnce && (
        <MusicPlayerTooltip
          position={tooltipPosition}
          onDismiss={onDismissMusicPlayerTooltip}
          onClick={() => {
            // Open the music player dropdown
            if (musicPlayerRef.current) {
              const rect = musicPlayerRef.current.getBoundingClientRect();
              const dropdownWidth = 200;
              const rightEdge = rect.right;
              const leftPosition = rightEdge - dropdownWidth;
              setMenuPosition({ x: Math.max(12, leftPosition), y: rect.bottom });
              setOpenMenu('music-player');
            }
          }}
        />
      )}
    </div>
  );
};

export default MenuBar;